import React from 'react';
import Annotation from 'components/text-editor/components/annotations';
import Form from 'components/form';
import useSurfaceNodeTextSnippets from 'routes/surface/hooks/use-surface-node-text-snippets';
import useScrollSnippetIntoView from 'components/text-editor/hooks/use-scroll-snippet-into-view';
import { useGlobalLinkOperations } from 'state/canvas/node-linking/global-links';
import { useFormikContext } from 'formik';
import { useTranslation } from 'i18n';
import { WithMargin } from 'components/layout';
import { notEmpty } from 'utils/js';
import { AnyObject } from 'types';
import { CustomEditor } from 'components/text-editor/hooks/use-custom-editor';
import { isEmpty } from 'lodash';

export interface NodeTextEditorProps {
  name: string;
  snippets: any;
  onSubmit: (values: AnyObject, actions: any, options?: any) => Promise<any>;
  closeForm: Function;
  isNote?: boolean;
  allGlobalLinkIndicatorsVisible?: boolean;
  readOnly?: boolean;
  onLoad?(editor: CustomEditor, editorValue: any): void;
}

const defaultProps = {};

function NodeTextEditor(props: NodeTextEditorProps) {
  const { t } = useTranslation();

  const form = useFormikContext();

  const initialValues: any = form.values;

  const scrollSnippetIntoView = useScrollSnippetIntoView();

  const { isGlobalLinkZoneEmpty } = useGlobalLinkOperations();

  const {
    snippets,
    onTextSnippetSave,
    onTextSnippetDelete,
    onStartLinking,
    onFinishLinking,
    onDeleteLink,
    onGlobalLinkIndicatorClick,
    saveSnippet,
    deleteSnippetByAnnotationId,
    isSnippetInGlobalLinkZone,
    showFinishLinkAction,
    shouldShowDeleteLinkAction,
  } = useSurfaceNodeTextSnippets(
    props.snippets,
    props.onSubmit,
    props.closeForm,
  );

  function onChange() {
    form.setFieldValue('snippets', Array.from(snippets.values()));
  }

  function customAnnotationRenderer({
    text,
    element,
    children,
    attributes,
    editorRenderedAt,
    deleteAnnotation,
    updateAnnotation,
    allGlobalLinkIndicatorsVisible,
  }) {
    const snippet = snippets.get(element.annotationId);

    function onGlobalLinkIndicatorClickHandler() {
      onGlobalLinkIndicatorClick({ annotationId: element.annotationId });
    }

    function deleteSnippet() {
      deleteSnippetByAnnotationId(element.annotationId);
      deleteAnnotation(element.annotationId);
    }

    function isNamed() {
      return snippet?.name && element.isNamed;
    }

    function isLink() {
      // We use this flag to show visual link to user before form is submitted, snippet created and placed in global link zone
      const linkCreatedInSameRender =
        editorRenderedAt && editorRenderedAt === element.renderedAt;

      return (
        linkCreatedInSameRender ||
        isSnippetInGlobalLinkZone(snippet) ||
        (notEmpty(snippet?.pointsTo) && element.isLink)
      );
    }

    return (
      <Annotation
        {...attributes}
        text={text}
        element={element}
        snippet={snippet}
        isLink={isLink()}
        isNamed={isNamed()}
        saveSnippet={saveSnippet}
        deleteSnippet={deleteSnippet}
        updateAnnotation={updateAnnotation}
        isGlobalLinkCountVisible={
          element.isSelected || allGlobalLinkIndicatorsVisible
        }
        globalLinkCount={snippet?.pointsTo?.length}
        onGlobalLinkIndicatorClick={onGlobalLinkIndicatorClickHandler}
        isNote={props.isNote}>
        {children}
      </Annotation>
    );
  }

  return (
    <WithMargin margin="30px" top="0" height="80%">
      <Form.TextEditor
        name={props.name}
        onChange={onChange}
        placeholder={t('Type your text here...')}
        allGlobalLinkIndicatorsVisible={props.allGlobalLinkIndicatorsVisible}
        customAnnotationRenderer={customAnnotationRenderer}
        onGlobalLinkIndicatorClick={onGlobalLinkIndicatorClick}
        onTextSnippetSave={onTextSnippetSave}
        onTextSnippetDelete={onTextSnippetDelete}
        onStartLinking={onStartLinking(form)}
        onFinishLinking={onFinishLinking(form)}
        showFinishLinkAction={showFinishLinkAction}
        showDeleteLinkAction={shouldShowDeleteLinkAction}
        onDeleteLink={onDeleteLink}
        readOnly={props.readOnly}
        isNote={props.isNote}
        onLoad={(editor) => {
          const annotations = editor.getAnnotations();
          // We wanna clear any leftover annotations in slate data for the following scenarios
          // 1. When there is a snippet that is deleted in DB
          // 2. When there is a link annotation created and user did not follow up on linking process and canceled linking
          annotations.forEach((annotation) => {
            const stillExistsInDB = initialValues.snippets.some(
              (snippet) => snippet.annotationId === annotation.annotationId,
            );

            const snippet = initialValues?.snippets.find(
              (snippet) => snippet.annotationId === annotation.annotationId,
            );

            const isCanceledLink =
              annotation.isLink &&
              isEmpty(snippet?.pointsTo) &&
              isGlobalLinkZoneEmpty();

            if (stillExistsInDB === false || isCanceledLink) {
              editor.deleteAnnotation(
                ['isLink', 'isName', 'name', 'annotationId'],
                annotation.annotationId,
              );
            }
          });

          scrollSnippetIntoView(editor, form);
        }}
      />
    </WithMargin>
  );
}

NodeTextEditor.defaultProps = defaultProps;

export default NodeTextEditor;
